import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
	DefaultButtonCompat as DefaultButton,
	DialogCompat as Dialog,
	DialogFooterCompat as DialogFooter,
	DialogType,
	MessageBarCompat as MessageBar,
	MessageBarType,
	PrimaryButtonCompat as PrimaryButton,
	TextCompat as Text,
} from 'components/FluentUICompat';
import {useSyncLatestGetexRegulationsMutation} from './hooks/GetexRegulations.generated';

type ResultState = 'success' | 'error' | null;

interface Props {
	isOpen: boolean;
	onDismiss: () => void;
}

export const SyncLatestDialog: React.FC<Props> = ({isOpen, onDismiss}) => {
	const {t} = useTranslation('features/getexregulation', {
		keyPrefix: 'SyncLatestDialog',
	});

	const [resultState, setResultState] = useState<ResultState>(null);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(
		undefined,
	);

	const [syncLatest, {loading}] = useSyncLatestGetexRegulationsMutation();

	const handleConfirm = async () => {
		try {
			const result = await syncLatest({
				variables: {
					syncAttachments: true,
				},
			});
			const resultObj =
				result.data?.syncLatestGetexRegulations
					?.syncLatestGetexRegulationsResult;
			const success = resultObj?.success === true;
			setResultState(success ? 'success' : 'error');
			setErrorMessage(resultObj?.message ?? undefined);
		} catch (e: unknown) {
			setResultState('error');
			setErrorMessage(e instanceof Error ? e.message : undefined);
		}
		onDismiss();
	};

	const handleDismiss = () => {
		onDismiss();
	};

	const handleResultClose = () => {
		setResultState(null);
		setErrorMessage(undefined);
	};

	return (
		<>
			<Dialog
				hidden={!isOpen}
				onDismiss={handleDismiss}
				dialogContentProps={{
					type: DialogType.normal,
					title: t('Title'),
				}}
			>
				<DialogFooter>
					<PrimaryButton
						onClick={handleConfirm}
						text={t('Confirm')}
						disabled={loading}
					/>
					<DefaultButton onClick={handleDismiss} text={t('Cancel')} />
				</DialogFooter>
			</Dialog>
			<Dialog
				hidden={resultState !== 'success'}
				onDismiss={handleResultClose}
				dialogContentProps={{
					type: DialogType.normal,
					title: t('SuccessTitle'),
				}}
			>
				<DialogFooter>
					<PrimaryButton onClick={handleResultClose} text={t('Ok')} />
				</DialogFooter>
			</Dialog>
			<Dialog
				hidden={resultState !== 'error'}
				onDismiss={handleResultClose}
				dialogContentProps={{
					type: DialogType.normal,
					title: t('ErrorTitle'),
				}}
			>
				<MessageBar messageBarType={MessageBarType.error} isMultiline>
					<Text block>{t('ErrorMessage')}</Text>
					{errorMessage && <Text block>{errorMessage}</Text>}
				</MessageBar>
				<DialogFooter>
					<PrimaryButton onClick={handleResultClose} text={t('Ok')} />
				</DialogFooter>
			</Dialog>
		</>
	);
};
